import { Component, createElement } from 'react';
import { DragSource, DropTarget } from 'react-dnd';

import PropTypes from 'prop-types';
import handleEvent from 'utils/handleEvent';
import DefaultFrame from './DefaultFrame';
import { WIDGET } from './ItemTypes';

const boxSource = {
  beginDrag(props) {
    return {
      type: 'widget',
      columnsLength: props.columnsLength,
      widgetName: props.widgetName,
      rowIndex: props.rowIndex,
      columnIndex: props.columnIndex,
      widgetIndex: props.widgetIndex,
      widgetProps: props.widgetProps,
    };
  },
};

const cardTarget = {
  hover(props, monitor) {
    const dragIndex = monitor.getItem().widgetIndex;
    const itemType = monitor.getItem().type;
    const hoverIndex = props.widgetIndex;

    if (itemType === 'column') {
      return;
    }
    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }
    // Time to actually perform the action
    const { columnIndex, rowIndex } = props;
    if (
      monitor.getItem().columnIndex === columnIndex &&
      monitor.getItem().rowIndex === rowIndex
    ) {
      props.onSortWidgets({
        initialLocation: {
          rowIndex,
          columnIndex,
          widgetIndex: dragIndex,
        },
        destination: {
          rowIndex,
          columnIndex,
          widgetIndex: hoverIndex,
        },
        widgetName: monitor.getItem().widgetName,
        widgetProps: monitor.getItem().widgetProps,
      });
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      monitor.getItem().widgetIndex = hoverIndex;
    }
  },
};

/**
 * Frame component which surrounds each widget.
 */
@DropTarget(WIDGET, cardTarget, (connect) => ({
  connectDropTarget: connect.dropTarget(),
}))
@DragSource(WIDGET, boxSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
}))
class WidgetFrame extends Component {
  remove = handleEvent(() => {
    const { rowIndex, columnIndex, widgetIndex } = this.props;
    this.props.onRemove({
      rowIndex,
      columnIndex,
      widgetIndex,
    });
  });

  handleEdit = handleEvent(() => {
    const { rowIndex, columnIndex, widgetIndex, onEdit } = this.props;
    onEdit({ rowIndex, columnIndex, widgetIndex });
  });

  render() {
    const {
      // isDragging,
      children,
      columnOrientation,
      connectDragSource,
      connectDropTarget,
      disableEdit,
      editable,
      frameComponent,
      msg,
      showTitle,
      title,
      widgetName,
    } = this.props;
    let selected = null;

    if (frameComponent) {
      // if user provided a custom frame,  use it
      selected = createElement(frameComponent, {
        children,
        disableEdit,
        editable,
        msg,
        onEdit: this.handleEdit,
        onRemove: this.remove,
        showTitle,
        title,
        widgetName,
      });
    } else {
      // else use the default frame
      selected = (
        <DefaultFrame editable={editable} onRemove={this.remove} title={title}>
          {children}
        </DefaultFrame>
      );
    }
    // const opacity = isDragging ? 0 : 1;
    const widgetFrame = (
      <div className={`dazzle-column-content-item-${columnOrientation}`}>
        {selected}
      </div>
    );

    return editable
      ? connectDragSource(connectDropTarget(widgetFrame))
      : widgetFrame;
  }
}

WidgetFrame.defaultProps = {
  disableEdit: false,
  showTitle: true,
};

WidgetFrame.propTypes = {
  /**
   * Childrens of the widget frame.
   */
  children: PropTypes.element,

  /**
   * Index of the column these widgets should be placed.
   */
  columnIndex: PropTypes.number,

  /**
   * Index of the row these widgets should be placed.
   */
  rowIndex: PropTypes.number,

  /**
   * Index of the widget.
   */
  widgetIndex: PropTypes.number,

  /**
   * Indicates weatehr dashboard is in ediable mode or not.
   */
  editable: PropTypes.bool,

  /**
   * User provided widget frame that should be used instead of the default one.
   */
  frameComponent: PropTypes.func,

  /**
   * Name of the widget.
   */
  widgetName: PropTypes.string,

  /**
   * Title of the widget.
   */
  title: PropTypes.string,

  /**
   * Weather the component is being dragged.
   */
  // isDragging: PropTypes.bool,

  /**
   * ReactDnd's connectDragSource().
   */
  connectDragSource: PropTypes.func,

  /**
   * ReactDnd's connectDropTarget().
   */
  connectDropTarget: PropTypes.func,

  /**
   * Function that should be called when a widget is about to be removed.
   */
  onRemove: PropTypes.func,

  showTitle: PropTypes.bool,
  columnOrientation: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  disableEdit: PropTypes.bool,
  msg: PropTypes.object.isRequired,
};

export default WidgetFrame;
